import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'

const ROAST_PROMPTS = [
  name => `${name} — the kind of person who sets a 6am alarm, turns it off, and tells people they "usually wake up early." You're not a morning person. You're not an evening person. You're a disappointment around the clock.`,
  name => `${name} has the energy of a participation trophy that got left in a garage sale. Someone priced you at 25 cents. Nobody bought you.`,
  name => `The audacity of ${name} to exist in the same timeline as genuinely interesting people is genuinely impressive. It takes talent to be this forgettable.`,
  name => `${name} is the human equivalent of buffering. Always loading. Never arriving. The little circle just keeps spinning.`,
  name => `${name} peaked in a dream they can't quite remember and have been coasting on the vibe ever since. The vibe is gone, ${name}. It left.`,
  name => `Scientists have studied ${name} and concluded that the energy you give off is best described as "laptop at 3%." Technically still on. Not really doing anything.`,
  name => `${name} is the kind of person who adds "lol" after saying something they meant seriously. The "lol" isn't protecting you. We heard you.`,
  name => `If ${name} were a font, they'd be Comic Sans — chosen by people with no options, regretted immediately, somehow still everywhere.`,
  name => `${name} has the personality of a terms and conditions page. Everyone scrolls past. Nobody reads it. You click agree just to make it stop.`,
  name => `The most dangerous thing about ${name} is their confidence. Not because they're capable — because they're not and they don't know it.`,
  name => `${name} gives off the energy of someone who discovered a band after they broke up and tells people they've "always been a fan."`,
  name => `${name} is built different. Just not differently good. Differently assembled. Like IKEA furniture where one step went wrong early and now nothing is level.`,
]

function getRoast(name) {
  const idx = (name.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % ROAST_PROMPTS.length
  return ROAST_PROMPTS[idx](name)
}

export default function AIRoast({ session }) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [roast, setRoast] = useState(null)
  const [loading, setLoading] = useState(false)
  const [glitching, setGlitching] = useState(false)
  const [scanLine, setScanLine] = useState(0)
  const [copied, setCopied] = useState(false)
  const [roastCount, setRoastCount] = useState(847)

  useEffect(() => {
    const sl = setInterval(() => setScanLine(s => (s + 1) % 100), 50)
    const rc = setInterval(() => setRoastCount(c => c + (Math.random() > 0.7 ? 1 : 0)), 3000)
    return () => { clearInterval(sl); clearInterval(rc) }
  }, [])

  const handleRoast = async () => {
    if (!name.trim()) return
    setLoading(true)
    setRoast(null)

    // Fake dramatic loading
    await new Promise(r => setTimeout(r, 1800))

    const result = getRoast(name.trim())

    // Glitch effect on reveal
    setGlitching(true)
    setTimeout(() => {
      setRoast(result)
      setGlitching(false)
      setLoading(false)
    }, 300)
  }

  const handleCopy = () => {
    const text = `${roast}\n\n— AI Roast Me @ 2dollarsites.com`
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleAgain = () => {
    setRoast(null)
    setName('')
  }

  return (
    <div style={{ minHeight: '100vh', background: '#000', color: '#fff', fontFamily: "'Share Tech Mono', monospace", position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Bebas+Neue&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}

        /* NEON GRID */
        .neon-grid{position:fixed;inset:0;background-image:linear-gradient(rgba(255,0,255,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,255,255,0.04) 1px,transparent 1px);background-size:40px 40px;pointer-events:none;z-index:0}

        /* SCAN LINE */
        .scan-line{position:fixed;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,rgba(0,255,255,0.15),transparent);pointer-events:none;z-index:5;transition:top 0.05s linear}

        /* CORNER GLOWS */
        .corner-glow-tl{position:fixed;top:-100px;left:-100px;width:400px;height:400px;border-radius:50%;background:radial-gradient(circle,rgba(255,0,255,0.08) 0%,transparent 70%);pointer-events:none;z-index:0}
        .corner-glow-br{position:fixed;bottom:-100px;right:-100px;width:400px;height:400px;border-radius:50%;background:radial-gradient(circle,rgba(0,255,255,0.06) 0%,transparent 70%);pointer-events:none;z-index:0}

        /* GLITCH ANIMATION */
        @keyframes glitch1{0%,100%{clip-path:inset(0 0 98% 0);transform:translate(-4px,0)}50%{clip-path:inset(30% 0 50% 0);transform:translate(4px,0)}}
        @keyframes glitch2{0%,100%{clip-path:inset(50% 0 30% 0);transform:translate(4px,0)}50%{clip-path:inset(80% 0 5% 0);transform:translate(-4px,0)}}
        @keyframes flicker{0%,100%{opacity:1}92%{opacity:1}93%{opacity:0.4}94%{opacity:1}97%{opacity:0.7}98%{opacity:1}}
        @keyframes pulse-neon{0%,100%{text-shadow:0 0 10px #ff00ff,0 0 20px #ff00ff,0 0 40px #ff00ff}50%{text-shadow:0 0 5px #ff00ff,0 0 10px #ff00ff}}
        @keyframes rotate-border{0%{background-position:0% 50%}100%{background-position:100% 50%}}
        @keyframes loading-bar{0%{width:0%}100%{width:100%}}
        @keyframes shake{0%,100%{transform:translate(0,0)}25%{transform:translate(-3px,2px)}75%{transform:translate(3px,-2px)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}

        .roast-name-input{width:100%;background:transparent;border:none;border-bottom:2px solid rgba(255,0,255,0.4);color:#ff00ff;font-family:'Share Tech Mono',monospace;font-size:20px;padding:14px 0;outline:none;transition:all 0.2s;text-align:center;letter-spacing:2px}
        .roast-name-input:focus{border-color:#ff00ff;text-shadow:0 0 10px rgba(255,0,255,0.5)}
        .roast-name-input::placeholder{color:rgba(255,0,255,0.2);letter-spacing:1px}

        .roast-btn{width:100%;background:linear-gradient(135deg,#ff00ff,#00ffff);color:#000;border:none;padding:18px;font-family:'Share Tech Mono',monospace;font-size:14px;letter-spacing:3px;font-weight:700;cursor:pointer;transition:all 0.2s;margin-top:20px;position:relative;overflow:hidden;text-transform:uppercase}
        .roast-btn::before{content:'';position:absolute;top:0;left:-100%;width:100%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.2),transparent);animation:shimmer 2s ease infinite}
        @keyframes shimmer{0%{left:-100%}100%{left:200%}}
        .roast-btn:hover{transform:scale(1.02);box-shadow:0 0 30px rgba(255,0,255,0.4),0 0 30px rgba(0,255,255,0.2)}
        .roast-btn:disabled{opacity:0.5;cursor:not-allowed;transform:none}

        .copy-btn{background:none;border:1px solid rgba(0,255,255,0.4);color:#00ffff;font-family:'Share Tech Mono',monospace;font-size:11px;padding:10px 20px;cursor:pointer;letter-spacing:2px;transition:all 0.2s}
        .copy-btn:hover{background:rgba(0,255,255,0.1);border-color:#00ffff;box-shadow:0 0 15px rgba(0,255,255,0.2)}
        .again-btn{background:none;border:1px solid rgba(255,0,255,0.4);color:#ff00ff;font-family:'Share Tech Mono',monospace;font-size:11px;padding:10px 20px;cursor:pointer;letter-spacing:2px;transition:all 0.2s}
        .again-btn:hover{background:rgba(255,0,255,0.1);border-color:#ff00ff}
        .back-btn{background:none;border:none;color:rgba(255,255,255,0.25);font-family:'Share Tech Mono',monospace;font-size:10px;letter-spacing:2px;cursor:pointer;padding:0;transition:color 0.2s}
        .back-btn:hover{color:rgba(255,255,255,0.6)}
      `}</style>

      <div className="neon-grid" />
      <div className="scan-line" style={{ top: `${scanLine}%` }} />
      <div className="corner-glow-tl" />
      <div className="corner-glow-br" />

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '32px 24px 80px', position: 'relative', zIndex: 2 }}>

        {/* TOP BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 40 }}>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← EXCHANGE</button>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', letterSpacing: 2 }}>
            ROASTS SERVED: <span style={{ color: 'rgba(255,0,255,0.6)' }}>{roastCount.toLocaleString()}</span>
          </div>
        </div>

        {/* TITLE */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 64,
            letterSpacing: 6,
            lineHeight: 1,
            marginBottom: 8,
            animation: 'flicker 4s ease infinite',
            background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 20px rgba(255,0,255,0.5))',
          }}>
            AI ROAST ME
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', letterSpacing: 3 }}>
            ENTER YOUR NAME. RECEIVE DESTRUCTION.
          </div>
        </div>

        {/* INPUT STATE */}
        {!roast && !loading && (
          <div style={{ animation: 'fadeIn 0.4s ease' }}>
            <div style={{ border: '1px solid rgba(255,0,255,0.15)', padding: '32px 28px', background: 'rgba(255,0,255,0.02)', position: 'relative' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg,transparent,rgba(255,0,255,0.5),transparent)' }} />
              <div style={{ fontSize: 9, color: 'rgba(255,0,255,0.5)', letterSpacing: 3, textAlign: 'center', marginBottom: 20 }}>// ENTER_TARGET</div>
              <input
                className="roast-name-input"
                placeholder="your name..."
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRoast()}
                autoComplete="off"
                maxLength={30}
              />
              <button className="roast-btn" onClick={handleRoast} disabled={!name.trim()}>
                ⚡ ROAST ME
              </button>
            </div>
            <div style={{ textAlign: 'center', marginTop: 16, fontSize: 10, color: 'rgba(255,255,255,0.15)', letterSpacing: 1, fontStyle: 'italic' }}>
              Warning: AI has no mercy and no filter.
            </div>
          </div>
        )}

        {/* LOADING STATE */}
        {loading && (
          <div style={{ border: '1px solid rgba(255,0,255,0.15)', padding: '40px 28px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'rgba(255,0,255,0.6)', letterSpacing: 3, marginBottom: 24 }}>
              ANALYZING {name.toUpperCase()}...
            </div>
            <div style={{ height: 2, background: 'rgba(255,0,255,0.1)', marginBottom: 6, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', background: 'linear-gradient(90deg,#ff00ff,#00ffff)', animation: 'loading-bar 1.8s ease forwards' }} />
            </div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)', letterSpacing: 2, marginTop: 16 }}>
              SCANNING FOR WEAKNESSES
            </div>
          </div>
        )}

        {/* ROAST RESULT */}
        {roast && !loading && (
          <div style={{ animation: 'fadeIn 0.5s ease' }}>
            {/* ROAST CARD */}
            <div style={{ border: '1px solid rgba(255,0,255,0.3)', padding: '32px 28px', background: 'rgba(255,0,255,0.03)', position: 'relative', marginBottom: 20 }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg,transparent,#ff00ff,transparent)', boxShadow: '0 0 10px rgba(255,0,255,0.5)' }} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg,transparent,rgba(0,255,255,0.4),transparent)' }} />

              {/* TARGET */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(255,0,255,0.1)' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#ff00ff', boxShadow: '0 0 10px #ff00ff', animation: 'pulse-neon 2s ease infinite' }} />
                <span style={{ fontSize: 10, color: 'rgba(255,0,255,0.6)', letterSpacing: 2 }}>TARGET ACQUIRED:</span>
                <span style={{ fontSize: 13, color: '#ff00ff', letterSpacing: 2, fontWeight: 700 }}>{name.toUpperCase()}</span>
              </div>

              {/* THE ROAST */}
              <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.9)', lineHeight: 1.85, letterSpacing: 0.3 }}>
                {roast}
              </div>

              {/* GLITCH DECORATIONS */}
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(0,255,255,0.08)', display: 'flex', justifyContent: 'space-between', fontSize: 9, color: 'rgba(255,255,255,0.15)', letterSpacing: 1 }}>
                <span>AI_ROAST_ME</span>
                <span>2DOLLARSITES.COM</span>
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <button className="copy-btn" style={{ flex: 1 }} onClick={handleCopy}>
                {copied ? '✓ COPIED' : '⧉ COPY TO SHARE'}
              </button>
              <button className="again-btn" style={{ flex: 1 }} onClick={handleAgain}>
                ↺ ROAST AGAIN
              </button>
            </div>

            <div style={{ textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,0.15)', letterSpacing: 1 }}>
              Screenshot and post it. Tag <span style={{ color: 'rgba(255,0,255,0.4)' }}>@2.dollar.sites</span>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
